import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { QrScanner, useCheckin } from "@/components/QrScanner";
import {
  Tent, Settings2, CalendarRange, ScanLine, Plus, Trash2, Save, Lock, MapPin,
  Layers, CheckCircle2, Banknote, ExternalLink, LogIn, LogOut, Upload, BellRing,
  CalendarDays, Percent, Globe,
} from "lucide-react";
import { ReservationCalendar } from "@/components/chef/ReservationCalendar";
import { ReservationSeasons } from "@/components/chef/ReservationSeasons";
import { ReservationExtras } from "@/components/chef/ReservationExtras";
import { ReservationChannels } from "@/components/chef/ReservationChannels";
import { ReservationAutomations } from "@/components/chef/ReservationAutomations";

const eur = (v) => `${(Number(v) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const dueOf = (b) => (b.payment_status === "paid_onsite" ? 0
  : b.payment_status === "paid" ? Number(b.balance_on_site || 0)
    : Number(b.total || 0));
const KIND_LABEL = { camping: "Camping", hotel: "Hôtel", chambre_hote: "Chambre d'hôte" };
const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

const EMPTY_UNIT = {
  label: "", unit_type: "", capacity: 2, price_per_night: 0,
  description: "", photo_url: "", map_x: null, map_y: null, amenities: [], active: true,
};

export default function ChefReservations() {
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [unitDialog, setUnitDialog] = useState(null);
  const [bulk, setBulk] = useState({ count: 10, prefix: "", start_at: 1, unit_type: "", capacity: 2, price_per_night: 0, replace_existing: false });
  const [range, setRange] = useState({ from: today(), to: plusDays(7) });
  const [avail, setAvail] = useState(null);
  const [arrivals, setArrivals] = useState(null);
  const [day, setDay] = useState(today());
  const [placing, setPlacing] = useState(null);
  const [drag, setDrag] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [sites, setSites] = useState(null); // null = multi-établissements désactivé
  const dragRef = useRef(false);

  useEffect(() => { api.get("/chef/sites").then((r) => setSites(r.data || [])).catch(() => setSites(null)); }, []);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/chef/minisite/reservation");
      setData(d);
      setCfg(d.config);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [tick]);
  useEffect(() => { load(); }, [load]);

  const loadAvail = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/chef/minisite/availability?date_from=${range.from}&date_to=${range.to}`);
      setAvail(d);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [range, tick]);
  useEffect(() => { loadAvail(); }, [loadAvail]);

  const loadArrivals = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/chef/minisite/arrivals?day=${day}`);
      setArrivals(d);
    } catch (e) { /* silencieux */ }
  }, [day, tick]);
  useEffect(() => { loadArrivals(); }, [loadArrivals]);

  const checkin = useCheckin(() => setTick((t) => t + 1));

  const saveCfg = async (override) => {
    const c = { ...cfg, ...(override || {}) };
    setSaving(true);
    try {
      await api.put("/chef/minisite/reservation", {
        ...c,
        min_nights: parseInt(c.min_nights) || 1,
        max_nights: parseInt(c.max_nights) || 30,
        deposit_pct: parseFloat(c.deposit_pct) || 0,
        tourist_tax_per_night: parseFloat(c.tourist_tax_per_night) || 0,
        reminder_hour: parseInt(c.reminder_hour ?? 10) || 0,
      });
      setTick((t) => t + 1);
      toast.success("Réglages de réservation enregistrés");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveUnit = async () => {
    const u = unitDialog;
    const body = {
      ...u,
      capacity: parseInt(u.capacity) || 1,
      price_per_night: parseFloat(u.price_per_night) || 0,
      map_x: u.map_x === null || u.map_x === "" ? null : parseFloat(u.map_x),
      map_y: u.map_y === null || u.map_y === "" ? null : parseFloat(u.map_y),
      amenities: typeof u.amenities === "string"
        ? u.amenities.split(",").map((s) => s.trim()).filter(Boolean) : (u.amenities || []),
    };
    try {
      if (u.id) await api.put(`/chef/minisite/units/${u.id}`, body);
      else await api.post("/chef/minisite/units", body);
      setUnitDialog(null);
      setTimeout(() => { document.body.style.pointerEvents = ""; }, 250);
      setTick((t) => t + 1);
      toast.success(u.id ? "Unité mise à jour" : "Unité créée");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delUnit = async (id) => {
    try {
      await api.delete(`/chef/minisite/units/${id}`);
      setTick((t) => t + 1);
      toast.success("Unité supprimée");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runBulk = async () => {
    try {
      const { data: r } = await api.post("/chef/minisite/units/bulk", {
        ...bulk,
        count: parseInt(bulk.count) || 1,
        start_at: parseInt(bulk.start_at) || 1,
        capacity: parseInt(bulk.capacity) || 1,
        price_per_night: parseFloat(bulk.price_per_night) || 0,
      });
      setTick((t) => t + 1);
      toast.success(`${r.created} ${labels.units.toLowerCase()} créé(e)s`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markPaid = async (bid) => {
    try {
      await api.patch(`/chef/minisite/bookings/${bid}/payment`);
      setTick((t) => t + 1);
      toast.success("Encaissement enregistré");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pctFrom = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    return {
      x: Math.min(100, Math.max(0, Math.round(((e.clientX - r.left) / r.width) * 1000) / 10)),
      y: Math.min(100, Math.max(0, Math.round(((e.clientY - r.top) / r.height) * 1000) / 10)),
    };
  };

  const savePin = async (u, x, y) => {
    try {
      await api.put(`/chef/minisite/units/${u.id}`, {
        label: u.label, unit_type: u.unit_type || "", capacity: u.capacity,
        price_per_night: u.price_per_night, description: u.description || "",
        photo_url: u.photo_url || "", map_x: x, map_y: y,
        amenities: u.amenities || [], active: u.active !== false,
      });
      setTick((t) => t + 1);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const placeOnMap = async (e) => {
    if (dragRef.current) { dragRef.current = false; return; }
    if (!placing) return;
    const { x, y } = pctFrom(e);
    const u = placing;
    setPlacing(null);
    await savePin(u, x, y);
    toast.success(`${u.label} positionné sur le plan`);
  };

  const onMapMove = (e) => {
    if (!drag) return;
    const { x, y } = pctFrom(e);
    setDrag((d) => (d ? { ...d, x, y, moved: true } : d));
  };

  const onMapUp = async () => {
    if (!drag) return;
    const d = drag;
    setDrag(null);
    dragRef.current = true;
    if (d.moved) {
      await savePin(d.unit, d.x, d.y);
      toast.success(`${d.unit.label} déplacé`);
    }
  };

  const uploadImage = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    const { data: r } = await api.post("/chef/minisite/media", fd,
      { headers: { "Content-Type": "multipart/form-data" } });
    return r.url;
  };

  const onMapFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading("map");
    try {
      const url = await uploadImage(f, "map");
      setCfg((c) => ({ ...c, map_photo_url: url }));
      await saveCfg({ map_photo_url: url });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(null); }
  };

  const onUnitFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading("unit");
    try {
      const url = await uploadImage(f, "unit");
      setUnitDialog((u) => ({ ...u, photo_url: url }));
      toast.success("Photo téléversée");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(null); }
  };

  if (!data) return <div className="p-10 text-sm text-[#868E96]" data-testid="cr-loading">Chargement…</div>;

  const labels = data.labels || { unit: "Unité", units: "Unités" };
  const optionOk = data.option?.subscribed;

  return (
    <div data-testid="chef-reservations-page">
      <PageHeader
        eyebrow="Réservations"
        title={`${KIND_LABEL[cfg?.kind] || "Hébergement"} — ${data.units.length} ${labels.units.toLowerCase()}`}
        sub="Vos clients réservent depuis votre mini-site, reçoivent leur QR code par email et le présentent à la réception."
        right={
          <a href="/chef/encaissement" data-testid="cr-link-encaissement">
            <Button variant="outline" className="h-10"><Banknote className="h-4 w-4 mr-1" />Encaissement</Button>
          </a>
        }
      />

      <div className="p-6 lg:p-10">
        {!optionOk && (
          <div className="mb-5 border-2 border-[#F59E0B] bg-[#FFFBEB] p-4 flex flex-wrap items-center gap-3"
               data-testid="cr-option-required">
            <Lock className="h-5 w-5 text-[#F59E0B]" />
            <div className="flex-1 min-w-[240px]">
              <div className="font-display font-bold text-sm">Option « {data.option?.name || "Réservation avancée"} » requise</div>
              <div className="text-xs text-[#495057]">
                La réservation en ligne s'active dès que cette option est ajoutée à votre abonnement
                {data.option?.monthly ? ` (${data.option.monthly} €/mois)` : ""}. Contactez PAG depuis
                PAG Helper pour l'activer.
              </div>
            </div>
            <a href="/chef/helper"><Button size="sm" variant="outline" data-testid="cr-ask-option">Demander l'option</Button></a>
          </div>
        )}

        <Tabs defaultValue={optionOk ? "arrivees" : "reglages"}>
          <TabsList data-testid="cr-tabs">
            <TabsTrigger value="arrivees" data-testid="cr-tab-arrivees"><ScanLine className="h-4 w-4 mr-1" />Arrivées & scan</TabsTrigger>
            <TabsTrigger value="calendrier" data-testid="cr-tab-calendrier"><CalendarDays className="h-4 w-4 mr-1" />Calendrier</TabsTrigger>
            <TabsTrigger value="planning" data-testid="cr-tab-planning"><CalendarRange className="h-4 w-4 mr-1" />Disponibilités</TabsTrigger>
            <TabsTrigger value="unites" data-testid="cr-tab-unites"><Tent className="h-4 w-4 mr-1" />{labels.units}</TabsTrigger>
            <TabsTrigger value="tarifs" data-testid="cr-tab-tarifs"><Percent className="h-4 w-4 mr-1" />Tarifs</TabsTrigger>
            <TabsTrigger value="canaux" data-testid="cr-tab-canaux"><Globe className="h-4 w-4 mr-1" />Booking & Airbnb</TabsTrigger>
            <TabsTrigger value="reglages" data-testid="cr-tab-reglages"><Settings2 className="h-4 w-4 mr-1" />Réglages</TabsTrigger>
          </TabsList>

          {/* ============ CALENDRIER ============ */}
          <TabsContent value="calendrier" className="mt-5" data-testid="cr-panel-calendrier">
            <ReservationCalendar labels={labels} onChanged={() => setTick((t) => t + 1)} />
          </TabsContent>

          {/* ============ TARIFS SAISONNIERS ============ */}
          <TabsContent value="tarifs" className="mt-5" data-testid="cr-panel-tarifs">
            {cfg && (
              <div className="space-y-5">
                <ReservationSeasons
                  cfg={cfg} setCfg={setCfg} saveCfg={saveCfg} saving={saving}
                  unitTypes={data.unit_types || []}
                  basePrice={data.units?.[0]?.price_per_night || 0}
                />
                <ReservationExtras cfg={cfg} setCfg={setCfg} saveCfg={saveCfg} saving={saving} />
              </div>
            )}
          </TabsContent>

          {/* ============ CANAUX (BOOKING / AIRBNB) ============ */}
          <TabsContent value="canaux" className="mt-5" data-testid="cr-panel-canaux">
            <ReservationChannels onChanged={() => setTick((t) => t + 1)} />
          </TabsContent>

          {/* ============ ARRIVÉES & SCAN ============ */}
          <TabsContent value="arrivees" className="mt-5" data-testid="cr-panel-arrivees">
            <div className="grid lg:grid-cols-[360px_1fr] gap-5">
              <div className="space-y-4">
                <QrScanner onToken={checkin.submit} disabled={checkin.busy} />
                {checkin.result && !checkin.result.error && (
                  <div className="border-2 border-[#059669] bg-[#ECFDF5] p-4" data-testid="cr-checkin-result">
                    <div className="flex items-center gap-2 font-display font-black text-lg">
                      <CheckCircle2 className="h-5 w-5 text-[#059669]" />
                      {checkin.result.already_arrived ? "Déjà arrivé" : "Arrivée enregistrée"}
                    </div>
                    <div className="text-sm mt-1">
                      <b>{checkin.result.booking.client_nom}</b> · {checkin.result.booking.unit_label}
                    </div>
                    <div className="text-xs text-[#495057]">
                      {checkin.result.booking.date_from} → {checkin.result.booking.date_to}
                      {" · "}{checkin.result.booking.guests} pers.
                    </div>
                    {(checkin.result.booking.extras || []).length > 0 && (
                      <div className="mt-2 border border-[#FFD700] bg-[#FFFBEB] p-2 text-xs"
                           data-testid="cr-checkin-extras">
                        <b>Options à préparer :</b>{" "}
                        {checkin.result.booking.extras
                          .map((x) => `${x.label}${x.qty > 1 ? ` ×${x.qty}` : ""}`).join(" · ")}
                      </div>
                    )}
                    <div className={`mt-2 text-sm font-bold ${checkin.result.payment_ok ? "text-[#059669]" : "text-[#E60000]"}`}
                         data-testid="cr-checkin-payment">
                      {checkin.result.payment_ok
                        ? "✓ Déjà payé — rien à encaisser"
                        : `À encaisser : ${eur(checkin.result.amount_to_collect)}`}
                    </div>
                    {!checkin.result.payment_ok && (
                      <Button size="sm" className="mt-2 bg-[#059669] text-white" data-testid="cr-checkin-collect"
                              onClick={() => { markPaid(checkin.result.booking.id); checkin.reset(); }}>
                        <Banknote className="h-4 w-4 mr-1" />J'ai encaissé
                      </Button>
                    )}
                  </div>
                )}
                {checkin.result?.error && (
                  <div className="border-2 border-[#E60000] bg-[#FEF2F2] p-3 text-sm text-[#7F1D1D]"
                       data-testid="cr-checkin-error">{checkin.result.error}</div>
                )}
              </div>

              <div>
                <div className="flex items-end gap-2 mb-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Journée</Label>
                    <Input type="date" value={day} onChange={(e) => setDay(e.target.value)}
                           className="h-10 w-[170px]" data-testid="cr-day" />
                  </div>
                  {arrivals && (
                    <div className="flex gap-2 pb-1">
                      {[
                        { k: "arrivals", l: "Arrivées", c: "#002FA7", I: LogIn },
                        { k: "departures", l: "Départs", c: "#F59E0B", I: LogOut },
                        { k: "onsite", l: "Sur place", c: "#059669", I: CheckCircle2 },
                      ].map((s) => (
                        <div key={s.k} className="border border-[#DEE2E6] bg-white px-3 py-1.5"
                             data-testid={`cr-count-${s.k}`}>
                          <div className="text-[9px] uppercase tracking-wider text-[#868E96] font-bold flex items-center gap-1">
                            <s.I className="h-3 w-3" />{s.l}
                          </div>
                          <div className="font-display font-black text-lg" style={{ color: s.c }}>
                            {arrivals.counts[s.k]}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {["arrivals", "departures", "onsite"].map((grp) => (
                  <div key={grp} className="mb-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96] mb-1">
                      {grp === "arrivals" ? "Arrivées du jour" : grp === "departures" ? "Départs du jour" : "Clients sur place"}
                    </div>
                    <div className="border border-[#DEE2E6] bg-white divide-y divide-[#DEE2E6]"
                         data-testid={`cr-list-${grp}`}>
                      {(arrivals?.[grp] || []).length === 0 ? (
                        <div className="p-3 text-xs text-[#868E96]">Aucun</div>
                      ) : arrivals[grp].map((b) => (
                        <div key={b.id} className="p-3 flex flex-wrap items-center gap-3" data-testid={`cr-booking-${b.reference}`}>
                          <div className="flex-1 min-w-[180px]">
                            <div className="text-sm font-semibold">{b.client_nom}</div>
                            <div className="text-[11px] text-[#868E96]">
                              {b.unit_label} · {b.guests} pers. · {b.date_from} → {b.date_to}
                            </div>
                          </div>
                          <code className="font-mono text-[10px]">{b.reference}</code>
                          {b.source === "api" && <span className="text-[9px] uppercase font-bold px-2 py-0.5 bg-[#FFD700] text-black" title={`Créée via l'API partenaire par ${b.device_name || b.device_id}`} data-testid={`cr-api-${b.reference}`}>via API · {b.device_name || b.device_id}</span>}
                          {b.cart_reminder_sent_at && b.status === "pending" && <span className="text-[9px] uppercase font-bold px-2 py-0.5 border border-[#868E96] text-[#868E96]" data-testid={`cr-reminded-${b.reference}`}>relancé</span>}
                          <span className={`text-[9px] uppercase font-bold px-2 py-0.5 ${
                            b.status === "arrived" ? "bg-[#059669] text-white"
                              : b.status === "cancelled" ? "bg-[#868E96] text-white" : "bg-[#002FA7] text-white"}`}>
                            {b.status === "arrived" ? "arrivé" : b.status === "confirmed" ? "confirmé" : b.status}
                          </span>
                          <span className={`text-[9px] uppercase font-bold px-2 py-0.5 ${
                            dueOf(b) <= 0 ? "bg-[#059669] text-white" : "bg-[#E60000] text-white"}`}>
                            {dueOf(b) <= 0
                              ? (b.payment_status === "paid" ? "payé en ligne" : "payé sur place")
                              : `${eur(dueOf(b))} dû`}
                          </span>
                          {dueOf(b) > 0 && (
                            <Button size="sm" variant="outline" className="text-[10px] h-7"
                                    data-testid={`cr-collect-${b.reference}`} onClick={() => markPaid(b.id)}>
                              Encaissé
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* ============ DISPONIBILITÉS ============ */}
          <TabsContent value="planning" className="mt-5" data-testid="cr-panel-planning">
            <div className="flex items-end gap-2 mb-4">
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Du</Label>
                <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
                       className="h-10 w-[160px]" data-testid="cr-range-from" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Au</Label>
                <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
                       className="h-10 w-[160px]" data-testid="cr-range-to" />
              </div>
              {avail && (
                <div className="border border-[#DEE2E6] bg-white px-4 py-2" data-testid="cr-avail-summary">
                  <div className="text-[9px] uppercase tracking-wider text-[#868E96] font-bold">Libres sur la période</div>
                  <div className="font-display font-black text-lg text-[#059669]">
                    {avail.available_count} / {avail.total_units}
                  </div>
                </div>
              )}
            </div>

            <div className="border border-[#DEE2E6] bg-white divide-y divide-[#DEE2E6]" data-testid="cr-avail-list">
              {(avail?.units || []).map((u) => (
                <div key={u.id} className="p-3 flex flex-wrap items-center gap-3" data-testid={`cr-avail-${u.id}`}>
                  <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${u.available ? "bg-[#059669]" : "bg-[#E60000]"}`} />
                  <div className="flex-1 min-w-[160px]">
                    <div className="text-sm font-semibold">{u.label}</div>
                    <div className="text-[11px] text-[#868E96]">
                      {u.unit_type || "—"} · {u.capacity} pers. · {eur(u.price_per_night)}/nuit
                      {!u.active && " · désactivée"}
                    </div>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    {(u.bookings || []).length === 0
                      ? <span className="text-[11px] text-[#059669] font-semibold">Libre</span>
                      : u.bookings.map((b) => (
                        <div key={b.booking_id} className="text-[11px] text-[#495057]">
                          <b>{b.client_nom}</b> · {b.date_from} → {b.date_to} ·{" "}
                          <code className="text-[10px]">{b.reference}</code>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
              {(avail?.units || []).length === 0 && (
                <div className="p-6 text-center text-sm text-[#868E96]" data-testid="cr-avail-empty">
                  Aucune {labels.unit.toLowerCase()} créée — allez dans l'onglet « {labels.units} ».
                </div>
              )}
            </div>
          </TabsContent>

          {/* ============ UNITÉS ============ */}
          <TabsContent value="unites" className="mt-5" data-testid="cr-panel-unites">
            {/* Création en masse */}
            <div className="border-2 border-[#002FA7] bg-[#F5F8FF] p-4 mb-5" data-testid="cr-bulk">
              <div className="font-display font-bold text-sm flex items-center gap-2 mb-2">
                <Layers className="h-4 w-4 text-[#002FA7]" />Créer {labels.units.toLowerCase()} en série
              </div>
              <div className="text-[11px] text-[#495057] mb-3">
                « J'ai 24 chambres » → indiquez 24, tout est généré numéroté d'un coup.
              </div>
              <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Combien</Label>
                  <Input type="number" min="1" max="200" value={bulk.count} data-testid="cr-bulk-count"
                         onChange={(e) => setBulk({ ...bulk, count: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Préfixe</Label>
                  <Input value={bulk.prefix} placeholder={labels.unit} data-testid="cr-bulk-prefix"
                         onChange={(e) => setBulk({ ...bulk, prefix: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">N° de départ</Label>
                  <Input type="number" min="0" value={bulk.start_at} data-testid="cr-bulk-start"
                         onChange={(e) => setBulk({ ...bulk, start_at: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Type</Label>
                  <Select value={bulk.unit_type || "none"} onValueChange={(v) => setBulk({ ...bulk, unit_type: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="cr-bulk-type"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {(data.unit_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Capacité</Label>
                  <Input type="number" min="1" value={bulk.capacity} data-testid="cr-bulk-capacity"
                         onChange={(e) => setBulk({ ...bulk, capacity: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Prix / nuit</Label>
                  <Input type="number" step="0.01" min="0" value={bulk.price_per_night} data-testid="cr-bulk-price"
                         onChange={(e) => setBulk({ ...bulk, price_per_night: e.target.value })} />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <Button onClick={runBulk} className="bg-[#002FA7] text-white" data-testid="cr-bulk-run">
                  <Plus className="h-4 w-4 mr-1" />Générer
                </Button>
                <button type="button" data-testid="cr-bulk-replace"
                        onClick={() => setBulk({ ...bulk, replace_existing: !bulk.replace_existing })}
                        className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1.5 border ${
                          bulk.replace_existing ? "bg-[#E60000] text-white border-[#E60000]" : "bg-white text-[#868E96] border-[#DEE2E6]"}`}>
                  {bulk.replace_existing ? "✓ " : ""}Remplacer l'existant
                </button>
                <Button variant="outline" onClick={() => setUnitDialog({ ...EMPTY_UNIT })} data-testid="cr-unit-new">
                  <Plus className="h-4 w-4 mr-1" />Ajouter une seule {labels.unit.toLowerCase()}
                </Button>
              </div>
            </div>

            {/* Plan cliquable (camping) */}
            {cfg?.kind === "camping" && (
              <div className="mb-5" data-testid="cr-map">
                <div className="flex flex-wrap items-end gap-2 mb-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider flex items-center gap-1">
                      <MapPin className="h-3 w-3" />Photo du plan du camping
                    </Label>
                    <label className="inline-flex items-center gap-2 h-10 px-4 border border-[#0A0A0A] bg-white
                                      text-xs font-bold uppercase tracking-wider cursor-pointer hover:bg-[#F8F9FA]"
                           data-testid="cr-map-upload-label">
                      <Upload className="h-4 w-4" />
                      {uploading === "map" ? "Téléversement…" : cfg.map_photo_url ? "Remplacer le plan" : "Téléverser le plan"}
                      <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                             data-testid="cr-map-upload" onChange={onMapFile} disabled={uploading === "map"} />
                    </label>
                  </div>
                  <div className="flex-1 min-w-[220px]">
                    <Label className="text-[10px] uppercase tracking-wider">…ou coller une URL d'image</Label>
                    <Input value={cfg.map_photo_url || ""} placeholder="https://…"
                           data-testid="cr-map-url"
                           onChange={(e) => setCfg({ ...cfg, map_photo_url: e.target.value })} />
                  </div>
                  <Button onClick={() => saveCfg()} disabled={saving} data-testid="cr-map-save" className="h-10">
                    <Save className="h-4 w-4 mr-1" />Enregistrer le plan
                  </Button>
                  {cfg.map_photo_url && (
                    <Button variant="outline" className="h-10 text-[#E60000]" data-testid="cr-map-clear"
                            onClick={() => { setCfg({ ...cfg, map_photo_url: "" }); saveCfg({ map_photo_url: "" }); }}>
                      <Trash2 className="h-4 w-4 mr-1" />Retirer
                    </Button>
                  )}
                </div>
                {cfg.map_photo_url ? (
                  <>
                    <div className="text-[11px] text-[#495057] mb-1">
                      {placing
                        ? <b className="text-[#E60000]">Cliquez sur le plan pour placer « {placing.label} »</b>
                        : <>Faites <b>glisser une pastille</b> pour la déplacer, ou cliquez sur « Placer »
                           à côté d'un emplacement puis sur le plan.</>}
                    </div>
                    <div className={`relative border-2 inline-block max-w-full select-none ${
                          placing ? "border-[#E60000] cursor-crosshair" : "border-[#DEE2E6]"}`}
                         onClick={placeOnMap} onPointerMove={onMapMove} onPointerUp={onMapUp}
                         onPointerLeave={onMapUp} data-testid="cr-map-canvas">
                      <img src={cfg.map_photo_url} alt="Plan du camping" draggable={false}
                           className="block max-w-full max-h-[440px] object-contain" />
                      {data.units.filter((u) => u.map_x != null && u.map_y != null).map((u) => {
                        const isDrag = drag?.id === u.id;
                        return (
                          <span key={u.id} data-testid={`cr-map-pin-${u.id}`}
                                onPointerDown={(e) => { e.stopPropagation(); setDrag({ id: u.id, unit: u, x: u.map_x, y: u.map_y }); }}
                                style={{ left: `${isDrag ? drag.x : u.map_x}%`, top: `${isDrag ? drag.y : u.map_y}%`, touchAction: "none" }}
                                className={`absolute -translate-x-1/2 -translate-y-1/2 text-[9px] font-bold px-1.5 py-0.5
                                            border border-white shadow cursor-grab active:cursor-grabbing ${
                                  isDrag ? "bg-[#002FA7] text-white scale-125" : "bg-[#E60000] text-white"}`}>
                            {u.label.replace(/[^0-9]/g, "") || u.label.slice(0, 3)}
                          </span>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="border border-dashed border-[#DEE2E6] p-6 text-center text-xs text-[#868E96]"
                       data-testid="cr-map-empty">
                    Téléversez la photo de votre plan (JPG, PNG ou WEBP, 8 Mo max) pour y positionner
                    vos emplacements à la souris.
                  </div>
                )}
              </div>
            )}

            {/* Liste des unités */}
            <div className="border border-[#DEE2E6] bg-white divide-y divide-[#DEE2E6]" data-testid="cr-units-list">
              {data.units.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#868E96]" data-testid="cr-units-empty">
                  Aucune {labels.unit.toLowerCase()} pour l'instant.
                </div>
              ) : data.units.map((u) => (
                <div key={u.id} className={`p-3 flex flex-wrap items-center gap-3 ${u.active ? "" : "opacity-60"}`}
                     data-testid={`cr-unit-${u.id}`}>
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-sm font-semibold">{u.label}</div>
                    <div className="text-[11px] text-[#868E96]">
                      {u.unit_type || "type non précisé"} · {u.capacity} pers.
                      {u.map_x != null && " · placé sur le plan"}
                      {!u.active && " · désactivée"}
                    </div>
                  </div>
                  <div className="font-display font-black">{eur(u.price_per_night)}<span className="text-[10px] font-normal text-[#868E96]">/nuit</span></div>
                  {cfg?.kind === "camping" && (
                    <Button size="sm" variant="outline" className="text-[10px] h-7"
                            data-testid={`cr-place-${u.id}`} onClick={() => setPlacing(u)}>
                      <MapPin className="h-3 w-3 mr-1" />Placer
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="text-[10px] h-7"
                          data-testid={`cr-edit-${u.id}`}
                          onClick={() => setUnitDialog({ ...u, amenities: (u.amenities || []).join(", ") })}>
                    Modifier
                  </Button>
                  <Button size="sm" variant="outline" className="text-[10px] h-7 text-[#E60000]"
                          data-testid={`cr-del-${u.id}`} onClick={() => delUnit(u.id)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ============ RÉGLAGES ============ */}
          <TabsContent value="reglages" className="mt-5" data-testid="cr-panel-reglages">
            {cfg && (
              <div className="border border-[#DEE2E6] bg-white p-5 max-w-3xl">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Type d'hébergement</Label>
                    <Select value={cfg.kind} onValueChange={(v) => setCfg({ ...cfg, kind: v })}>
                      <SelectTrigger data-testid="cr-cfg-kind"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(data.kinds || []).map((k) => <SelectItem key={k} value={k}>{KIND_LABEL[k] || k}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="text-[10px] text-[#868E96] mt-1">
                      Change le vocabulaire et les types proposés (emplacements vs chambres).
                    </div>
                  </div>
                  <div className="flex items-end">
                    <button type="button" data-testid="cr-cfg-enabled"
                            onClick={() => setCfg({ ...cfg, enabled: !cfg.enabled })}
                            className={`h-10 px-4 text-[11px] uppercase tracking-wider font-bold border ${
                              cfg.enabled ? "bg-[#059669] text-white border-[#059669]" : "bg-white text-[#868E96] border-[#DEE2E6]"}`}>
                      {cfg.enabled ? "✓ Réservation en ligne active" : "Réservation en ligne désactivée"}
                    </button>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Heure d'arrivée</Label>
                    <Input type="time" value={cfg.checkin_time} data-testid="cr-cfg-checkin"
                           onChange={(e) => setCfg({ ...cfg, checkin_time: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Heure de départ</Label>
                    <Input type="time" value={cfg.checkout_time} data-testid="cr-cfg-checkout"
                           onChange={(e) => setCfg({ ...cfg, checkout_time: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Nuits minimum</Label>
                    <Input type="number" min="1" value={cfg.min_nights} data-testid="cr-cfg-min"
                           onChange={(e) => setCfg({ ...cfg, min_nights: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Nuits maximum</Label>
                    <Input type="number" min="1" value={cfg.max_nights} data-testid="cr-cfg-max"
                           onChange={(e) => setCfg({ ...cfg, max_nights: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Taxe de séjour (€ / nuit / pers.)</Label>
                    <Input type="number" step="0.01" min="0" value={cfg.tourist_tax_per_night} data-testid="cr-cfg-tax"
                           onChange={(e) => setCfg({ ...cfg, tourist_tax_per_night: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Acompte à la réservation (%)</Label>
                    <Input type="number" step="1" min="0" max="100" value={cfg.deposit_pct} data-testid="cr-cfg-deposit"
                           onChange={(e) => setCfg({ ...cfg, deposit_pct: e.target.value })} />
                    <div className="text-[10px] text-[#868E96] mt-1">0 = le client règle la totalité.</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 mt-4">
                  {[
                    { key: "allow_online_payment", label: "Carte bancaire (Stripe)" },
                    { key: "allow_paypal_payment", label: "PayPal" },
                    { key: "allow_onsite_payment", label: "Paiement sur place" },
                  ].map((t) => (
                    <button key={t.key} type="button" data-testid={`cr-cfg-${t.key}`}
                            onClick={() => setCfg((c) => ({ ...c, [t.key]: !c[t.key] }))}
                            className={`text-[11px] uppercase tracking-wider font-bold px-3 py-2 border ${
                              cfg[t.key] ? "bg-[#059669] text-white border-[#059669]" : "bg-white text-[#868E96] border-[#DEE2E6]"}`}>
                      {cfg[t.key] ? "✓ " : "✗ "}{t.label}
                    </button>
                  ))}
                </div>
                <div className="text-[11px] text-[#495057] mt-2">
                  Carte bancaire et PayPal versent l'argent <b>directement sur vos comptes</b> —{" "}
                  <a href="/chef/encaissement" className="underline text-[#002FA7]" data-testid="cr-cfg-encaissement-link">
                    les connecter <ExternalLink className="inline h-3 w-3" />
                  </a>
                  . Tant qu'un moyen n'est pas connecté, il n'apparaît pas chez vos clients.
                </div>

                {/* Rappel de la veille */}
                <div className="mt-5 border-t border-[#DEE2E6] pt-4" data-testid="cr-cfg-reminder">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96] flex items-center gap-1 mb-2">
                    <BellRing className="h-3 w-3" />Rappel la veille de l'arrivée
                  </div>
                  <div className="flex flex-wrap items-end gap-3">
                    <button type="button" data-testid="cr-cfg-reminder-enabled"
                            onClick={() => setCfg((c) => ({ ...c, reminder_enabled: !c.reminder_enabled }))}
                            className={`h-10 px-3 text-[11px] uppercase tracking-wider font-bold border ${
                              cfg.reminder_enabled !== false ? "bg-[#059669] text-white border-[#059669]" : "bg-white text-[#868E96] border-[#DEE2E6]"}`}>
                      {cfg.reminder_enabled !== false ? "✓ Email de rappel" : "✗ Email de rappel"}
                    </button>
                    <button type="button" data-testid="cr-cfg-reminder-sms"
                            onClick={() => setCfg((c) => ({ ...c, reminder_sms: !c.reminder_sms }))}
                            className={`h-10 px-3 text-[11px] uppercase tracking-wider font-bold border ${
                              cfg.reminder_sms ? "bg-[#059669] text-white border-[#059669]" : "bg-white text-[#868E96] border-[#DEE2E6]"}`}>
                      {cfg.reminder_sms ? "✓ SMS en plus" : "✗ SMS en plus"}
                    </button>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider">Heure d'envoi (Paris)</Label>
                      <Select value={String(cfg.reminder_hour ?? 10)}
                              onValueChange={(v) => setCfg({ ...cfg, reminder_hour: parseInt(v) })}>
                        <SelectTrigger className="w-[110px]" data-testid="cr-cfg-reminder-hour"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, h) => (
                            <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="text-[11px] text-[#495057] mt-2">
                    Le client reçoit son QR code, son code de confirmation et l'heure d'arrivée.
                    Un rappel ne part qu'une fois par réservation. Le SMS consomme votre crédit Twilio.
                  </div>
                </div>

                <ReservationAutomations cfg={cfg} setCfg={setCfg} />

                <div className="mt-4">
                  <Label className="text-[10px] uppercase tracking-wider">Conditions de réservation (affichées au client)</Label>
                  <Textarea rows={3} value={cfg.cgv || ""} data-testid="cr-cfg-cgv"
                            onChange={(e) => setCfg({ ...cfg, cgv: e.target.value })} />
                </div>

                <Button onClick={() => saveCfg()} disabled={saving} className="mt-4 bg-[#002FA7] text-white" data-testid="cr-cfg-save">
                  <Save className="h-4 w-4 mr-1" />{saving ? "Enregistrement…" : "Enregistrer les réglages"}
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog unité */}
      <Dialog open={!!unitDialog} onOpenChange={(o) => { if (!o) { setUnitDialog(null); setTimeout(() => { document.body.style.pointerEvents = ""; }, 250); } }}>
        <DialogContent data-testid="cr-unit-dialog">
          <DialogHeader><DialogTitle>{unitDialog?.id ? "Modifier" : "Nouvelle"} {labels.unit.toLowerCase()}</DialogTitle></DialogHeader>
          {unitDialog && (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Nom / numéro</Label>
                  <Input value={unitDialog.label} data-testid="cr-unit-label"
                         onChange={(e) => setUnitDialog({ ...unitDialog, label: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Type</Label>
                  <Select value={unitDialog.unit_type || "none"}
                          onValueChange={(v) => setUnitDialog({ ...unitDialog, unit_type: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="cr-unit-type"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {(data.unit_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Capacité (personnes)</Label>
                  <Input type="number" min="1" value={unitDialog.capacity} data-testid="cr-unit-capacity"
                         onChange={(e) => setUnitDialog({ ...unitDialog, capacity: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Prix par nuit (€)</Label>
                  <Input type="number" step="0.01" min="0" value={unitDialog.price_per_night} data-testid="cr-unit-price"
                         onChange={(e) => setUnitDialog({ ...unitDialog, price_per_night: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Description</Label>
                <Textarea rows={2} value={unitDialog.description || ""} data-testid="cr-unit-desc"
                          onChange={(e) => setUnitDialog({ ...unitDialog, description: e.target.value })} />
              </div>
              {sites !== null && sites.length > 0 && (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Établissement</Label>
                  <Select value={unitDialog.site_id || "none"}
                          onValueChange={(v) => setUnitDialog({ ...unitDialog, site_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="cr-unit-site"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Non rattachée —</SelectItem>
                      {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Équipements (séparés par des virgules)</Label>
                <Input value={typeof unitDialog.amenities === "string" ? unitDialog.amenities : (unitDialog.amenities || []).join(", ")}
                       placeholder="électricité, eau, ombrage, wifi" data-testid="cr-unit-amenities"
                       onChange={(e) => setUnitDialog({ ...unitDialog, amenities: e.target.value })} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Photo</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex items-center gap-2 h-10 px-3 border border-[#0A0A0A] bg-white
                                    text-[11px] font-bold uppercase tracking-wider cursor-pointer hover:bg-[#F8F9FA]"
                         data-testid="cr-unit-photo-upload-label">
                    <Upload className="h-4 w-4" />
                    {uploading === "unit" ? "Téléversement…" : "Téléverser"}
                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                           data-testid="cr-unit-photo-upload" onChange={onUnitFile} disabled={uploading === "unit"} />
                  </label>
                  <Input value={unitDialog.photo_url || ""} placeholder="…ou URL de l'image"
                         className="flex-1 min-w-[180px]" data-testid="cr-unit-photo"
                         onChange={(e) => setUnitDialog({ ...unitDialog, photo_url: e.target.value })} />
                </div>
                {unitDialog.photo_url && (
                  <img src={unitDialog.photo_url} alt="" data-testid="cr-unit-photo-preview"
                       className="mt-2 h-24 w-full object-cover border border-[#DEE2E6]" />
                )}
              </div>
              <button type="button" data-testid="cr-unit-active"
                      onClick={() => setUnitDialog({ ...unitDialog, active: !unitDialog.active })}
                      className={`text-[11px] uppercase tracking-wider font-bold px-3 py-2 border ${
                        unitDialog.active ? "bg-[#059669] text-white border-[#059669]" : "bg-white text-[#868E96] border-[#DEE2E6]"}`}>
                {unitDialog.active ? "✓ Réservable" : "✗ Hors service"}
              </button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUnitDialog(null); setTimeout(() => { document.body.style.pointerEvents = ""; }, 250); }}
                    data-testid="cr-unit-cancel">Annuler</Button>
            <Button onClick={saveUnit} className="bg-[#002FA7] text-white" data-testid="cr-unit-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
